import { cx } from '@emotion/css';
import { useFormContext } from 'react-hook-form';
import { PropsWithChildren, useState } from 'react';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { InputField, InputFieldProps } from '@phoenix/design-system/frontend/ui/form/input/input-field';
import { useTranslation } from 'next-i18next';
import PasswordStrengthMeter from './password-strength-meter';
import { HintProps } from '@phoenix/design-system/frontend/ui/form/hint';

const eyeIcon = getDesignSystemIcon('icon-eye-16.svg');
const eyeClosedIcon = getDesignSystemIcon('icon-eye-closed-16.svg');

export const HookFormPasswordInput = ({
  name,
  className,
  passwordStrength = false,
  type = 'password',
  validateOnSubmit = false,
  blockedWords = [],
  ...restProps
}: PropsWithChildren<
  InputFieldProps &
    Omit<InputFieldProps, 'type'> & {
      type: 'password' | 'text';
      name: string;
      passwordStrength?: boolean;
      blockedWords?: string[];
    }
>) => {
  const {
    register,
    watch,
    formState: { errors, dirtyFields, touchedFields },
  } = useFormContext();
  const { t, i18n } = useTranslation('shared');
  const [inputType, setInputType] = useState(type);
  // handle password icon toggle and regular icons
  const icon = type === 'password' ? (inputType === 'password' ? eyeIcon : eyeClosedIcon) : restProps?.icon;
  const language = i18n.language.split('-')[0];

  const isDirtyOrTouched = (fieldName: string) => {
    if (validateOnSubmit) {
      return true;
    }
    return dirtyFields[fieldName] || touchedFields[fieldName];
  };

  const getHint = (fieldName: string): HintProps | undefined => {
    if (errors[fieldName] && isDirtyOrTouched(fieldName)) {
      if (errors[fieldName]?.type === 'passwordStrength') {
        return {
          message: '',
          type: 'error',
          dataTestId: `${name}ValidationErrorMessage`,
        };
      }
      return {
        message: errors[fieldName]?.message,
        type: 'error',
        dataTestId: `${name}ValidationErrorMessage`,
      };
    }
    return undefined;
  };

  return (
    <>
      <InputField
        hint={getHint(name)}
        iconClickAction={() => {
          if (type === 'password') {
            setInputType(inputType === 'password' ? 'text' : 'password');
          }
        }}
        icon={icon}
        type={inputType}
        className={cx(className, passwordStrength ? 'mb-2 lg:mb-s' : 'mb-4 lg:mb-m')}
        {...restProps}
        label={restProps.label ? `${restProps.label} ${!restProps.required ? t('shared_formControlLabel_optionalSuffix') : ''}` : undefined}
        {...register(name)}
        SrOnlyComponent={
          inputType === 'password' ? (
            <span className="sr-only">{t('shared_password_toggle_show_aria')}</span>
          ) : (
            <span className="sr-only">{t('shared_password_toggle_hide_aria')}</span>
          )
        }
      />

      {passwordStrength && <PasswordStrengthMeter password={watch(name)} language={language} blockedWords={blockedWords} />}
    </>
  );
};
